import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import {
  getSensors,
  getSensorAlarms,
  getSensorConnectivity,
  getPCOStateChanges,
  countAlertTransitions,
  countAlarmShutoffs,
} from "../unitEventsUtils";

export type AggregateRow = {
  unitsCount: number | null;
  alertsCount: number | null;
  shutdownsCount: number | null;
  hasPCO: boolean;
  aggStatus: "loading" | "ready" | "error";
};

export type PortfolioTotals = {
  properties: number;
  units: number;
  onlineSensors: number;
  pcoEquipped: number;
  alerts: number;
  shutdowns: number;
};

export type BuildingAggregatesResult = {
  aggregates: Map<string, AggregateRow>;
  /** Flat list of every alarm record across every sensor, for charting. */
  allAlertRecords: any[];
  totals: PortfolioTotals;
};

type Building = {
  buildingId: string;
  name: string;
  LatLong: [number, number];
};

const ALERT_PAGE_SIZE = 5000;
const REFETCH_INTERVAL = 60_000 * 60; // 1h, matches Sensors view

/**
 * Fan-out per-building → fetch sensor lists, then per-sensor session totals
 * within the chosen date window, then aggregate into Units + Alerts counts.
 *
 * Returns a `Map<buildingId, AggregateRow>` whose reference is stable across
 * renders unless a query's data actually changed (uses `dataUpdatedAt` +
 * `status` as the memo signature).
 */
export function useBuildingAggregates(
  buildings: Building[],
  startDate: string | null,
  endDate: string | null
): BuildingAggregatesResult {
  // Step 1: sensors per building.
  const sensorQueries = useQueries({
    queries: buildings.map((b) => ({
      queryKey: ["sensors", b.buildingId],
      queryFn: async () => await getSensors(b.buildingId),
      staleTime: 60_000,
      refetchInterval: REFETCH_INTERVAL,
    })),
  });

  // Flatten sensors so we can fan-out one session query per sensor.
  type SensorRef = { buildingId: string; name: string };
  const sensorRefs: SensorRef[] = [];
  buildings.forEach((b, i) => {
    const q = sensorQueries[i];
    const list = q?.data?.data ?? [];
    list.forEach((s: { name: string }) => {
      if (s?.name) sensorRefs.push({ buildingId: b.buildingId, name: s.name });
    });
  });

  // Step 2: alert history per sensor. The alarms_v2 endpoint doesn't accept a
  // date filter, so we cache one fetch per sensor and filter client-side.
  const sessionQueries = useQueries({
    queries: sensorRefs.map((s) => ({
      queryKey: ["alerts-agg", s.name],
      queryFn: async () => await getSensorAlarms(s.name, ALERT_PAGE_SIZE),
      enabled: !!s.name,
      refetchInterval: REFETCH_INTERVAL,
    })),
  });

  // Step 3: PCO state + connectivity per sensor — match the Sensors page
  // "stove control" logic: a sensor is "controllable" only when it's currently
  // online AND its latest PCO state reports the unit connected. Building-level
  // hasPCO is true when at least one sensor at the building meets that bar.
  const pcoQueries = useQueries({
    queries: sensorRefs.map((s) => ({
      queryKey: ["pco-presence", s.name],
      queryFn: async () => await getPCOStateChanges(s.name, 1),
      enabled: !!s.name,
      staleTime: 5 * 60_000,
    })),
  });
  const connectivityQueries = useQueries({
    queries: sensorRefs.map((s) => ({
      queryKey: ["connectivity-presence", s.name],
      queryFn: async () => await getSensorConnectivity(s.name, 1),
      enabled: !!s.name,
      staleTime: 60_000,
    })),
  });

  // Memo signatures — these only change when query data/status actually
  // changes, so the returned Map is stable across no-op renders.
  const sensorSig = sensorQueries
    .map((q) => `${q.status}:${q.dataUpdatedAt ?? 0}`)
    .join("|");
  const sessionSig = sessionQueries
    .map((q) => `${q.status}:${q.dataUpdatedAt ?? 0}`)
    .join("|");
  const pcoSig = pcoQueries
    .map((q) => `${q.status}:${q.dataUpdatedAt ?? 0}`)
    .join("|");
  const connSig = connectivityQueries
    .map((q) => `${q.status}:${q.dataUpdatedAt ?? 0}`)
    .join("|");
  const buildingSig = buildings.map((b) => b.buildingId).join("|");

  return useMemo(() => {
    const result = new Map<string, AggregateRow>();
    const allAlertRecords: any[] = [];
    sessionQueries.forEach((q) => {
      const records = q?.data?.data;
      if (Array.isArray(records)) allAlertRecords.push(...records);
    });

    let onlineSensors = 0;
    let pcoEquipped = 0;
    sensorRefs.forEach((_, idx) => {
      const conn = connectivityQueries[idx]?.data?.data?.[0];
      const pco = pcoQueries[idx]?.data?.data?.[0];
      const online = conn?.eventType === "connected";
      const pcoConnected = pco?.currentUnitConnectedStatus === 1;
      if (online) onlineSensors += 1;
      if (online && pcoConnected) pcoEquipped += 1;
    });

    buildings.forEach((b, i) => {
      const sensorsQ = sensorQueries[i];
      if (!sensorsQ) {
        result.set(b.buildingId, {
          unitsCount: null,
          alertsCount: null,
          shutdownsCount: null,
          hasPCO: false,
          aggStatus: "loading",
        });
        return;
      }
      if (sensorsQ.isError) {
        result.set(b.buildingId, {
          unitsCount: null,
          alertsCount: null,
          shutdownsCount: null,
          hasPCO: false,
          aggStatus: "error",
        });
        return;
      }
      if (sensorsQ.isPending) {
        result.set(b.buildingId, {
          unitsCount: null,
          alertsCount: null,
          shutdownsCount: null,
          hasPCO: false,
          aggStatus: "loading",
        });
        return;
      }

      const sensorList: { name: string }[] = sensorsQ.data?.data ?? [];
      const unitsCount = sensorList.length;

      const sessionIdxs: number[] = [];
      sensorRefs.forEach((s, idx) => {
        if (s.buildingId === b.buildingId) sessionIdxs.push(idx);
      });

      if (unitsCount === 0) {
        result.set(b.buildingId, {
          unitsCount: 0,
          alertsCount: 0,
          shutdownsCount: 0,
          hasPCO: false,
          aggStatus: "ready",
        });
        return;
      }

      const buildingPcoQs = sessionIdxs.map((i2) => pcoQueries[i2]);
      const buildingConnQs = sessionIdxs.map((i2) => connectivityQueries[i2]);
      // Match the Sensors page's deriveStoveControl logic: a sensor is
      // controllable only when it's currently online AND has a PCO connected.
      const hasPCO = sessionIdxs.some((_, idxInBuilding) => {
        const conn = buildingConnQs[idxInBuilding]?.data?.data?.[0];
        const pco = buildingPcoQs[idxInBuilding]?.data?.data?.[0];
        const online = conn?.eventType === "connected";
        const pcoConnected = pco?.currentUnitConnectedStatus === 1;
        return online && pcoConnected;
      });

      const buildingSessionQs = sessionIdxs.map((i2) => sessionQueries[i2]);
      const anyError = buildingSessionQs.some((q) => q?.isError);
      const anyLoading = buildingSessionQs.some(
        (q) => q?.isPending || q?.fetchStatus === "fetching"
      );

      if (anyError) {
        result.set(b.buildingId, {
          unitsCount,
          alertsCount: null,
          shutdownsCount: null,
          hasPCO,
          aggStatus: "error",
        });
        return;
      }
      if (!startDate || !endDate || anyLoading) {
        result.set(b.buildingId, {
          unitsCount,
          alertsCount: null,
          shutdownsCount: null,
          hasPCO,
          aggStatus: "loading",
        });
        return;
      }

      let alertsCount = 0;
      let shutdownsCount = 0;
      buildingSessionQs.forEach((q) => {
        const records = q?.data?.data ?? [];
        alertsCount += countAlertTransitions(records, startDate, endDate);
        shutdownsCount += countAlarmShutoffs(records, startDate, endDate);
      });

      result.set(b.buildingId, {
        unitsCount,
        alertsCount,
        shutdownsCount,
        hasPCO,
        aggStatus: "ready",
      });
    });

    let totalUnits = 0;
    let totalAlerts = 0;
    let totalShutdowns = 0;
    result.forEach((row) => {
      totalUnits += row.unitsCount ?? 0;
      totalAlerts += row.alertsCount ?? 0;
      totalShutdowns += row.shutdownsCount ?? 0;
    });

    return {
      aggregates: result,
      allAlertRecords,
      totals: {
        properties: buildings.length,
        units: totalUnits,
        onlineSensors,
        pcoEquipped,
        alerts: totalAlerts,
        shutdowns: totalShutdowns,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingSig, sensorSig, sessionSig, pcoSig, connSig, startDate, endDate]);
}
